import { useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react'
import { formatUsd, signum } from '../format/decimal.js'
import { fullTime } from '../format/time.js'
import { cx } from './cx.js'

export interface SeriesPoint {
  /** ISO timestamp */
  t: string
  /** decimal string (USD) */
  v: string
}

export interface SparklineProps {
  points: readonly SeriesPoint[]
  width?: number
  height?: number
  /** what the series is, for the accessible summary and tooltip ("Equity") */
  label: string
  /** colour by direction of change (default) or neutral ink */
  tone?: 'auto' | 'neutral' | 'brand'
  /** `chart`: taller, with baseline, min/max ticks, keyboard scrubbing */
  variant?: 'inline' | 'chart'
  /** draw a dashed zero line when the series crosses zero (pnl) */
  zeroLine?: boolean
  className?: string
}

/**
 * One series, 2px line, end dot (chart mode adds a flat 6 % area). Hover (and arrow keys in chart
 * mode) scrub a crosshair with an exact-value readout. Single series: the
 * label names it, no legend.
 */
export function Sparkline({ points, width = 120, height = 32, label, tone = 'auto', variant = 'inline', zeroLine, className }: SparklineProps) {
  const [hover, setHover] = useState<number | null>(null)
  const ref = useRef<SVGSVGElement>(null)
  const chart = variant === 'chart'
  const padY = chart ? 10 : 3
  const padX = chart ? 2 : 2

  const geo = useMemo(() => {
    const vals = points.map((p) => Number(p.v))
    const min = Math.min(...vals, zeroLine ? 0 : Infinity)
    const max = Math.max(...vals, zeroLine ? 0 : -Infinity)
    const span = max - min || 1
    const x = (i: number) => padX + (points.length <= 1 ? (width - 2 * padX) / 2 : (i / (points.length - 1)) * (width - 2 * padX))
    const y = (v: number) => padY + (1 - (v - min) / span) * (height - 2 * padY)
    const pts = vals.map((v, i) => [x(i), y(v)] as const)
    const line = pts.map(([px, py], i) => `${i ? 'L' : 'M'}${px.toFixed(1)} ${py.toFixed(1)}`).join(' ')
    const area = pts.length ? `${line} L${pts[pts.length - 1]![0].toFixed(1)} ${height} L${pts[0]![0].toFixed(1)} ${height} Z` : ''
    return { pts, line, area, min, max, y }
  }, [points, width, height, padX, padY, zeroLine])

  if (points.length === 0) {
    return (
      <svg width={chart ? '100%' : width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className={className} role="img" aria-label={`${label}: no data yet`}>
        <line x1={2} x2={width - 2} y1={height / 2} y2={height / 2} stroke="var(--line-strong)" strokeDasharray="3 3" />
      </svg>
    )
  }

  const first = points[0]!
  const last = points[points.length - 1]!
  const dir = signum(String(Number(last.v) - Number(first.v)))
  const color = tone === 'neutral' ? 'var(--ink-2)' : tone === 'brand' ? 'var(--brand)' : dir > 0 ? 'var(--gain)' : dir < 0 ? 'var(--loss)' : 'var(--ink-3)'
  const summary = `${label}: ${formatUsd(first.v)} to ${formatUsd(last.v)} over ${points.length} points, ${dir > 0 ? 'up' : dir < 0 ? 'down' : 'flat'}`
  const active = hover ?? null
  const ap = active !== null ? geo.pts[active] : null

  const onMove = (e: PointerEvent<SVGSVGElement>) => {
    const r = ref.current?.getBoundingClientRect()
    if (!r) return
    const rel = ((e.clientX - r.left) / r.width) * width
    let best = 0
    let bd = Infinity
    geo.pts.forEach(([px], i) => {
      const d = Math.abs(px - rel)
      if (d < bd) {
        bd = d
        best = i
      }
    })
    setHover(best)
  }
  const onKey = (e: KeyboardEvent<SVGSVGElement>) => {
    if (!chart) return
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault()
      const d = e.key === 'ArrowRight' ? 1 : -1
      setHover((h) => Math.max(0, Math.min(points.length - 1, (h ?? points.length - 1) + d)))
    } else if (e.key === 'Escape') setHover(null)
  }

  const zeroY = zeroLine && geo.min < 0 && geo.max > 0 ? geo.y(0) : null
  const tip = active !== null ? points[active]! : null
  const tipLeft = ap ? Math.min(Math.max(ap[0] / width, 0.12), 0.88) : 0

  return (
    <span className={cx('relative inline-block align-middle', chart && 'w-full', className)} style={chart ? undefined : { width, height }}>
      <svg
        ref={ref}
        width={chart ? '100%' : width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={summary}
        tabIndex={chart ? 0 : undefined}
        onPointerMove={onMove}
        onPointerLeave={() => setHover(null)}
        onKeyDown={onKey}
        onBlur={() => setHover(null)}
        className="block touch-none overflow-visible"
      >
        {chart && (
          <g stroke="var(--line)" strokeWidth={1} vectorEffect="non-scaling-stroke">
            <line x1={0} x2={width} y1={padY} y2={padY} strokeDasharray="2 4" vectorEffect="non-scaling-stroke" />
            <line x1={0} x2={width} y1={height - padY} y2={height - padY} vectorEffect="non-scaling-stroke" />
          </g>
        )}
        {zeroY !== null && <line x1={0} x2={width} y1={zeroY} y2={zeroY} stroke="var(--ink-3)" strokeDasharray="3 3" strokeWidth={1} vectorEffect="non-scaling-stroke" />}
        {chart && <path d={geo.area} fill={color} fillOpacity={0.06} />}
        <path d={geo.line} fill="none" stroke={color} strokeWidth={chart ? 2 : 1.6} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        {ap ? (
          <g>
            <line x1={ap[0]} x2={ap[0]} y1={0} y2={height} stroke="var(--ink-3)" strokeWidth={1} vectorEffect="non-scaling-stroke" />
          </g>
        ) : null}
      </svg>
      {/* markers in HTML so they stay round under preserveAspectRatio=none */}
      {(() => {
        const p = ap ?? geo.pts[geo.pts.length - 1]!
        const s = chart ? 9 : 6
        return (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute rounded-full"
            style={{ left: `calc(${(p[0] / width) * 100}% - ${s / 2}px)`, top: p[1] - s / 2, width: s, height: s, background: color, boxShadow: '0 0 0 2px var(--surface)' }}
          />
        )
      })()}
      {tip && (
        <span
          role="status"
          className="pointer-events-none absolute z-10 -translate-x-1/2 whitespace-nowrap rounded-md border border-line bg-surface px-2 py-1 text-[11.5px] shadow-[var(--shadow-3)]"
          style={{ left: `${tipLeft * 100}%`, bottom: height + 6 }}
        >
          <span className="block font-mono text-[11px] text-ink-3">{fullTime(tip.t)}</span>
          <span className="num font-semibold text-ink">
            {label} {formatUsd(tip.v)}
          </span>
        </span>
      )}
      {chart && (
        <>
          <span className="num pointer-events-none absolute right-0 top-0 -translate-y-full pb-0.5 font-mono text-[10px] text-ink-3">{formatUsd(String(geo.max), { compact: true })}</span>
          <span className="num pointer-events-none absolute bottom-0 right-0 translate-y-full pt-0.5 font-mono text-[10px] text-ink-3">{formatUsd(String(geo.min), { compact: true })}</span>
        </>
      )}
    </span>
  )
}
